import React, { useEffect, useMemo, useState } from 'react';
import {
    CCard,
    CCardBody,
    CCol,
    CRow,
    CButton,
    CFormInput,
    CFormTextarea,
    CFormSelect,
    CFormCheck,
    CFormSwitch,
    CTable,
    CTableHead,
    CTableRow,
    CTableHeaderCell,
    CTableBody,
    CTableDataCell,
    CBadge,
    CTooltip,
    CSpinner,
} from '@coreui/react-pro';
import CIcon from '@coreui/icons-react';
import {
    cilArrowLeft,
    cilArrowRight,
    cilCheck,
    cilPlus,
    cilTrash,
    cilLayers,
    cilTag,
    cilListFilter,
    cilImage,
    cilStar,
    cilReload,
    cilCloudUpload,
    cilSearch,
    cilMenu,
} from '@coreui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import {
    ProductOption,
    ProductOptionValue,
    ProductVariant,
    ProductImage,
    ProductFormData,
    Addon,
    ProductAddonLink,
    CreateProductPayload,
    ProductToppingGroup,
} from '../../types/product-form';
import ToppingsStep, { validateToppingGroups } from './ToppingsStep';
import * as DomicilappService from "../../services/DomicilappService";
import { useStore } from '../../contexts/StoreContext';
import { useNotification } from '../../contexts/NotificationContext';
import { CategoryItem } from '../../types/category';
// import { useStore } from 'react-redux';

// -------------------------------------------------------------------------
// Helper: generador de ids temporales para el cliente (antes de persistir)
// -------------------------------------------------------------------------
const genId = (prefix: string) => `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;

// Producto cartesiano de arrays (usado para generar variantes desde opciones)
function cartesian<T>(...arrays: T[][]): T[][] {
    return arrays.reduce<T[][]>((acc, curr) => acc.flatMap((a) => curr.map((c) => [...a, c])), [[]]);
}

// Clave estable de una combinación: ids de sus valores ordenados (no depende del orden de las opciones)
const comboKey = (valueIds: string[]) => valueIds.map(String).sort().join('|');

// Una combinación quitada excluye también las que la contienen (ej. si después se agrega otra opción)
const isComboRemoved = (valueIds: string[], removed: Set<string>) => {
    const ids = new Set(valueIds.map(String));
    return Array.from(removed).some((key) => key.split('|').every((id) => ids.has(id)));
};

// const CATEGORIES = [
//     { id: '1', name: 'Pizzas Especiales' },
//     { id: '2', name: 'Hamburguesas' },
//     { id: '3', name: 'Bebidas' },
// ];

// El catálogo de adicionales ahora se carga en vivo desde GET /stores/{storeId}/addons
// (ver useEffect dentro del componente), scoped por tienda.

export const ProductCreateWizard: React.FC = () => {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState<number>(1);
    const { storeId } = useStore();
    // /products/edit/:id reutiliza el wizard en modo edición
    const { id: productId } = useParams<{ id: string }>();
    const isEdit = !!productId;
    const [loadingProduct, setLoadingProduct] = useState<boolean>(isEdit);
    const [saving, setSaving] = useState<boolean>(false);
    const { showModal, showToast } = useNotification();

    // ===================== ESTADO GENERAL DEL PRODUCTO =====================
    const [formData, setFormData] = useState<ProductFormData>({
        name: '',
        description: '',
        category_id: 0,
        is_active: true,
        has_variants: true,
        base_price: 15000,
        base_stock: 50,
        base_sku: '',
        packaging_fee: 0,
        brand: '',
        products_units_measurement_id: 1,
    });

    // ===================== GALERÍA DE IMÁGENES (subida desde el PC) =====================
    const [images, setImages] = useState<ProductImage[]>([]);
    const [categories, setCategories] = useState<CategoryItem[]>([]);

    // Libera los blob: URLs cuando el componente se desmonta, para no dejar memory leaks
    useEffect(() => {
        return () => {
            images.forEach((img) => {
                if (img.file) URL.revokeObjectURL(img.preview_url);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilesSelected = (fileList: FileList | null) => {
        if (!fileList || fileList.length === 0) return;
        const accepted = Array.from(fileList).filter((f) => f.type.startsWith('image/'));
        setImages((prev) => {
            const newOnes: ProductImage[] = accepted.map((file, i) => ({
                id: genId('img'),
                file,
                preview_url: URL.createObjectURL(file),
                position: prev.length + i,
                is_main: prev.length === 0 && i === 0,
            }));
            return [...prev, ...newOnes];
        });
    };

    const handleSetMainImage = (id: string) => {
        setImages((prev) => prev.map((img) => ({ ...img, is_main: img.id === id })));
    };

    const handleRemoveImage = (id: string) => {
        setImages((prev) => {
            const target = prev.find((i) => i.id === id);
            if (target?.file) URL.revokeObjectURL(target.preview_url);
            const filtered = prev.filter((img) => img.id !== id);
            if (filtered.length > 0 && !filtered.some((i) => i.is_main)) {
                filtered[0] = { ...filtered[0], is_main: true };
            }
            return filtered;
        });
        // Si alguna variante usaba esta imagen como override, la liberamos
        setVariants((prev) => prev.map((v) => (v.image_id === id ? { ...v, image_id: null } : v)));
    };

    // ===================== OPCIONES (ejes de variación) =====================
    const [options, setOptions] = useState<ProductOption[]>([]);
    // const [options, setOptions] = useState<ProductOption[]>([
    //     {
    //         id: genId('opt'),
    //         name: 'Tamaño',
    //         position: 0,
    //         values: [
    //             { id: genId('val'), value: 'Personal', position: 0 },
    //             { id: genId('val'), value: 'Familiar', position: 1 },
    //         ],
    //     },
    // ]);
    const [newOptionName, setNewOptionName] = useState('');

    const handleAddOption = () => {
        if (!newOptionName.trim()) return;
        const exists = options.some((o) => o.name.toLowerCase() === newOptionName.trim().toLowerCase());
        if (exists) return;
        setOptions((prev) => [...prev, { id: genId('opt'), name: newOptionName.trim(), position: prev.length, values: [] }]);
        setNewOptionName('');
    };

    // ===================== ORDEN DE OPCIONES Y VALORES (arrastrar) =====================
    // El orden en pantalla es el que se guarda (position) y el que ve el cliente en la app.
    // Las variantes se regeneran en este orden al salir del paso.
    const [dragging, setDragging] = useState<{ optionId: string; valueId?: string } | null>(null);
    const [dragOverId, setDragOverId] = useState<string | null>(null);

    const moveItem = <T,>(list: T[], from: number, to: number): T[] => {
        const next = [...list];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        return next;
    };

    const startDrag = (e: React.DragEvent, item: { optionId: string; valueId?: string }, preview?: Element | null) => {
        e.stopPropagation();
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', item.valueId ?? item.optionId); // Firefox no arrastra sin datos
        if (preview) e.dataTransfer.setDragImage(preview, 20, 20);
        setDragging(item);
    };

    const endDrag = () => {
        setDragging(null);
        setDragOverId(null);
    };

    // Solo se suelta una opción sobre otra opción, o un valor sobre otro valor de la misma opción
    const canDropOn = (optionId: string, valueId?: string) =>
        !!dragging && (valueId ? dragging.optionId === optionId && !!dragging.valueId : !dragging.valueId);

    const dragOverHandlers = (optionId: string, valueId?: string) => ({
        onDragOver: (e: React.DragEvent) => {
            if (!canDropOn(optionId, valueId)) return;
            e.preventDefault();
            e.stopPropagation();
            setDragOverId(valueId ?? optionId);
        },
        onDrop: (e: React.DragEvent) => {
            if (!canDropOn(optionId, valueId)) return;
            e.preventDefault();
            e.stopPropagation();
            if (valueId) handleValueDrop(optionId, valueId);
            else handleOptionDrop(optionId);
            endDrag();
        },
    });

    const handleOptionDrop = (targetOptionId: string) => {
        const sourceId = dragging?.optionId;
        setOptions((prev) => {
            const from = prev.findIndex((o) => o.id === sourceId);
            const to = prev.findIndex((o) => o.id === targetOptionId);
            if (from < 0 || to < 0 || from === to) return prev;
            return moveItem(prev, from, to).map((o, index) => ({ ...o, position: index }));
        });
    };

    const handleValueDrop = (optionId: string, targetValueId: string) => {
        const sourceId = dragging?.valueId;
        setOptions((prev) =>
            prev.map((opt) => {
                if (opt.id !== optionId) return opt;
                const from = opt.values.findIndex((v) => v.id === sourceId);
                const to = opt.values.findIndex((v) => v.id === targetValueId);
                if (from < 0 || to < 0 || from === to) return opt;
                return { ...opt, values: moveItem(opt.values, from, to).map((v, index) => ({ ...v, position: index })) };
            })
        );
    };

    const handleRemoveOption = (optionId: string) => {
        setOptions((prev) => prev.filter((o) => o.id !== optionId));
    };

    const handleAddOptionValue = (optionId: string, valueStr: string) => {
        const clean = valueStr.trim();
        if (!clean) return;
        setOptions((prev) =>
            prev.map((opt) => {
                if (opt.id !== optionId) return opt;
                const duplicate = opt.values.some((v) => v.value.toLowerCase() === clean.toLowerCase());
                if (duplicate) return opt;
                const newValue: ProductOptionValue = { id: genId('val'), value: clean, position: opt.values.length };
                return { ...opt, values: [...opt.values, newValue] };
            })
        );
    };

    const handleRemoveOptionValue = (optionId: string, valueId: string) => {
        setOptions((prev) =>
            prev.map((opt) => (opt.id === optionId ? { ...opt, values: opt.values.filter((v) => v.id !== valueId) } : opt))
        );
        setVariants((prev) => prev.filter((v) => v.option_value_ids[optionId] !== valueId));
        // Si alguna imagen estaba tageada a este valor, la dejamos sin etiquetar
        setImages((prev) => prev.map((img) => (img.option_value_id === valueId ? { ...img, option_value_id: null } : img)));
    };

    // Etiqueta/desetiqueta una imagen de la galería con un valor de opción (ej. "Rojo").
    // Una imagen solo puede representar UN valor a la vez: si ya estaba tageada a otro
    // valor, se reasigna. Varias imágenes SÍ pueden compartir el mismo valor.
    const toggleImageOptionValue = (imageId: string, valueId: string) => {
        setImages((prev) =>
            prev.map((img) => {
                if (img.id !== imageId) return img;
                return { ...img, option_value_id: img.option_value_id === valueId ? null : valueId };
            })
        );
    };

    const optionValueLabel = (valueId: string | null | undefined): string | null => {
        if (!valueId) return null;
        for (const opt of options) {
            const found = opt.values.find((v) => v.id === valueId);
            if (found) return found.value;
        }
        return null;
    };

    // Resuelve qué imagen le corresponde a una variante, buscando entre sus valores
    // de opción cuál tiene alguna imagen tageada. Si ninguno tiene, cae a la principal.
    const resolveVariantImage = (variant: ProductVariant): ProductImage | undefined => {
        const variantValueIds = Object.values(variant.option_value_ids);
        const tagged = images.find((img) => img.option_value_id && variantValueIds.includes(img.option_value_id));
        return tagged || mainImage;
    };

    // ===================== VARIANTES GENERADAS =====================
    const [variants, setVariants] = useState<ProductVariant[]>([]);
    // Combinaciones que la tienda quitó porque no existen (ej. Premium + Ardiente). No son variantes
    // inactivas: no se guardan y la matriz no las vuelve a generar.
    const [removedCombos, setRemovedCombos] = useState<Set<string>>(new Set());
    const [bulkPrice, setBulkPrice] = useState<number | ''>('');
    const [bulkStock, setBulkStock] = useState<number | ''>('');
    const [expandedDescriptions, setExpandedDescriptions] = useState<Set<string>>(new Set());

    const toggleDescriptionRow = (variantId: string) => {
        setExpandedDescriptions((prev) => {
            const next = new Set(prev);
            if (next.has(variantId)) next.delete(variantId);
            else next.add(variantId);
            return next;
        });
    };

    const buildSku = (comboValues: string[]) => {
        const base = (formData.base_sku || formData.name.substring(0, 3) || 'PROD').toUpperCase().replace(/\s+/g, '');
        const suffix = comboValues.map((v) => v.substring(0, 3).toUpperCase()).join('-');
        return `${base}-${suffix}`;
    };

    const generateVariantsFromOptions = (removed: Set<string> = removedCombos) => {
        const usableOptions = options.filter((o) => o.values.length > 0);
        if (usableOptions.length === 0) {
            setVariants([]);
            return;
        }

        const optionValueGroups = usableOptions.map((opt) =>
            opt.values.map((v) => ({ optionId: opt.id, optionName: opt.name, valueId: v.id, value: v.value }))
        );

        const combinations = cartesian(...optionValueGroups).filter(
            (combo) => !isComboRemoved(combo.map((c) => c.valueId), removed)
        );

        setVariants((prevVariants) => {
            return combinations.map((combo) => {
                const option_value_ids: Record<string, string> = {};
                const options_map: Record<string, string> = {};
                combo.forEach((item) => {
                    option_value_ids[item.optionId] = item.valueId;
                    options_map[item.optionName] = item.value;
                });

                const existing = prevVariants.find((v) => {
                    const keys = Object.keys(option_value_ids);
                    if (keys.length !== Object.keys(v.option_value_ids).length) return false;
                    return keys.every((k) => v.option_value_ids[k] === option_value_ids[k]);
                });

                if (existing) return existing;

                return {
                    id: genId('var'),
                    sku: buildSku(combo.map((c) => c.value)),
                    price: formData.base_price || 0,
                    compare_at_price: null,
                    stock: formData.base_stock || 0,
                    is_available: true,
                    option_value_ids,
                    options_map,
                    image_id: null,
                } as ProductVariant;
            });
        });
    };

    const updateVariant = (id: string, patch: Partial<ProductVariant>) => {
        setVariants((prev) => prev.map((v) => (v.id === id ? { ...v, ...patch } : v)));
    };

    // Quita una combinación que no existe: deja de guardarse (en edición la variante se elimina)
    const removeCombination = (variant: ProductVariant) => {
        if (variants.length <= 1) {
            showToast({ title: 'No se puede quitar', message: 'El producto debe tener al menos una combinación', type: 'warning', delay: 3500 });
            return;
        }
        setRemovedCombos((prev) => new Set(prev).add(comboKey(Object.values(variant.option_value_ids))));
        setVariants((prev) => prev.filter((v) => v.id !== variant.id));
    };

    // Vuelve a generar las combinaciones quitadas (las que siguen en la matriz conservan sus datos)
    const restoreRemovedCombinations = () => {
        const none = new Set<string>();
        setRemovedCombos(none);
        generateVariantsFromOptions(none);
    };

    // Valores de opción que no quedaron en ninguna combinación (el cliente los vería sin poder elegirlos)
    const orphanValues = useMemo(() => {
        if (!formData.has_variants || variants.length === 0) return [];
        const used = new Set(variants.flatMap((v) => Object.values(v.option_value_ids).map(String)));
        return options.flatMap((opt) => opt.values.filter((val) => !used.has(String(val.id))).map((val) => `${opt.name}: ${val.value}`));
    }, [formData.has_variants, variants, options]);

    const regenerateSku = (id: string) => {
        setVariants((prev) => prev.map((v) => (v.id === id ? { ...v, sku: buildSku(Object.values(v.options_map)) } : v)));
    };

    const applyBulkPrice = () => {
        if (bulkPrice === '') return;
        setVariants((prev) => prev.map((v) => ({ ...v, price: Number(bulkPrice) })));
    };

    const applyBulkStock = () => {
        if (bulkStock === '') return;
        setVariants((prev) => prev.map((v) => ({ ...v, stock: Number(bulkStock) })));
    };

    // ===================== ADICIONALES (addons + product_addons) =====================
    const [addonCatalog, setAddonCatalog] = useState<Addon[]>([]);
    const [addonSearch, setAddonSearch] = useState('');
    const [linkedAddons, setLinkedAddons] = useState<ProductAddonLink[]>([]);
    const [newAddonName, setNewAddonName] = useState('');
    const [newAddonPrice, setNewAddonPrice] = useState(0);

    // Extrae el array de addons sin importar si tu backend responde plano
    // ([...]) o paginado ({ data: [...], current_page, total, ... }, típico
    // de Laravel cuando mandas ?per_page=).
    const extractAddonsArray = (response: any): Addon[] => {
        console.log("extractAddonsArray");
        console.log(response);
        console.log(response?.data);
        const payload = response?.data;
        console.log("payload['items']");
        console.log(payload['items']);

        return payload['items'];
        // if (Array.isArray(payload)) return payload;
        // if (Array.isArray(payload?.data)) return payload['items'];
        // return [];
    };

    useEffect(() => {
        if (!storeId) return;
        DomicilappService.getAddonsByStore(storeId)
            .then((response: any) => setAddonCatalog(extractAddonsArray(response)))
            .catch((err: any) => console.error('Error cargando el catálogo de adicionales', err));
    }, [storeId]);


    useEffect(() => {
        if (!storeId) return;
        DomicilappService.getAllCategories()
            .then((response: any) => setCategories(response.data.items))
            .catch((err: any) => console.error('Error cargando el categorias', err));
    }, [storeId]);


    const isLinked = (addonId: string | number) => linkedAddons.some((l) => l.addon_id === addonId);

    const toggleLinkAddon = (addon: Addon) => {
        setLinkedAddons((prev) => {
            if (prev.some((l) => l.addon_id === addon.id)) {
                return prev.filter((l) => l.addon_id !== addon.id);
            }
            return [...prev, { id: genId('link'), addon_id: addon.id, name: addon.name, price: addon.price, is_default: false }];
        });
    };

    const toggleDefaultAddon = (linkId: string) => {
        setLinkedAddons((prev) => prev.map((l) => (l.id === linkId ? { ...l, is_default: !l.is_default } : l)));
    };

    const removeLinkedAddon = (linkId: string) => {
        setLinkedAddons((prev) => prev.filter((l) => l.id !== linkId));
    };

    const [creatingAddon, setCreatingAddon] = useState(false);

    const handleCreateAddon = async () => {
        const clean = newAddonName.trim();
        if (!clean) return;

        const exists = addonCatalog.find((a) => a.name.toLowerCase() === clean.toLowerCase());
        if (exists) {
            // ya existe en el catálogo: solo lo vinculamos, no duplicamos
            if (!isLinked(exists.id)) toggleLinkAddon(exists);
            setNewAddonName('');
            setNewAddonPrice(0);
            return;
        }

        try {
            setCreatingAddon(true);

            // Tu backend ya devuelve el registro completo al crear, así que lo usamos
            // directo, sin round-trip extra a recargar todo el catálogo.
            const createResponse: any = await DomicilappService.createAddon(storeId, { name: clean, price: newAddonPrice });
            const created: Addon | undefined = createResponse?.data;

            if (created?.id) {
                setAddonCatalog((prev) => [...prev, created]);
                toggleLinkAddon(created);
            } else {
                console.error('La respuesta de createAddon no trajo el registro esperado:', createResponse);
                alert('El adicional se pudo haber creado, pero la respuesta no fue la esperada. Revisa el catálogo.');
            }
        } catch (err) {
            console.error('Error creando el adicional', err);
            alert('No se pudo crear el adicional. Intenta de nuevo.');
        } finally {
            setCreatingAddon(false);
            setNewAddonName('');
            setNewAddonPrice(0);
        }
    };

    // ===================== TOPPINGS (toppings_groups + products_toppings_groups) =====================
    const [toppingGroups, setToppingGroups] = useState<ProductToppingGroup[]>([]);

    // Variantes a las que se les pueden dar reglas de toppings propias
    const toppingVariants = useMemo(
        () => (formData.has_variants ? variants.map((v) => ({ id: v.id, label: Object.values(v.options_map).join(' / ') || v.sku })) : []),
        [formData.has_variants, variants]
    );

    // ===================== CARGA DEL PRODUCTO (modo edición) =====================
    // Los ids reales se pasan a string: así conviven con los temporales del wizard y el
    // backend distingue qué actualizar (numérico) y qué crear (temporal).
    useEffect(() => {
        if (!isEdit || !storeId || !productId) return;
        setLoadingProduct(true);
        DomicilappService.getProductForm(storeId, productId)
            .then((response) => {
                const data = response?.data;
                if (response?.status !== 200 || !data?.product) {
                    showToast({ title: 'Producto no disponible', message: response?.message ?? 'No se encontró el producto en esta tienda', type: 'danger', delay: 3500 });
                    navigate('/products/list');
                    return;
                }
                setFormData({ ...data.product, base_sku: data.product.base_sku ?? '', packaging_fee: Number(data.product.packaging_fee ?? 0) });
                setImages(
                    data.images.map((img) => ({
                        id: String(img.id),
                        preview_url: img.url,
                        url: img.url,
                        position: img.position,
                        is_main: img.is_main,
                        option_value_id: img.option_value_id ? String(img.option_value_id) : null,
                    }))
                );
                setOptions(
                    data.options.map((opt) => ({
                        id: String(opt.id),
                        name: opt.name,
                        position: opt.position,
                        values: opt.values.map((v) => ({ id: String(v.id), value: v.value, position: v.position })),
                    }))
                );
                setVariants(data.variants.map((v) => ({ ...v, id: String(v.id) })));
                // Las combinaciones posibles que no están guardadas son las que la tienda quitó
                const savedKeys = new Set(data.variants.map((v) => comboKey(Object.values(v.option_value_ids))));
                const usableValues = data.options.filter((opt) => opt.values.length > 0).map((opt) => opt.values.map((v) => String(v.id)));
                setRemovedCombos(
                    data.product.has_variants && usableValues.length > 0
                        ? new Set(cartesian(...usableValues).map(comboKey).filter((key) => !savedKeys.has(key)))
                        : new Set()
                );
                setLinkedAddons(data.addons.map((a) => ({ ...a, id: String(a.id) })));
                setToppingGroups(
                    data.toppings_groups.map((g) => ({
                        ...g,
                        id: String(g.id),
                        items: g.items.map((i) => ({ ...i, client_id: genId('ti') })),
                        variant_rules: (g.variant_rules ?? []).map((r) => ({ ...r, variant_id: String(r.variant_id) })),
                    }))
                );
            })
            .catch((err) => {
                console.error('Error cargando el producto', err);
                showToast({ title: '¡Ha ocurrido un error!', message: 'No fue posible cargar el producto', type: 'danger', delay: 3500 });
            })
            .finally(() => setLoadingProduct(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEdit, storeId, productId]);

    // Al crear, si no se ha elegido categoría, se preselecciona la primera
    useEffect(() => {
        if (!isEdit && categories.length > 0 && !formData.category_id) {
            setFormData((prev) => ({ ...prev, category_id: categories[0].id }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories, isEdit]);

    const filteredCatalog = useMemo(
        () => addonCatalog.filter((a) => a.name.toLowerCase().includes(addonSearch.trim().toLowerCase())),
        [addonCatalog, addonSearch]
    );

    // ===================== NAVEGACIÓN =====================
    const LAST_STEP = 6;

    // Al salir del paso de opciones se recalcula la matriz (conserva las variantes existentes)
    const goToStep = (step: number) => {
        if (currentStep === 2 && step !== 2 && formData.has_variants) {
            generateVariantsFromOptions();
        }
        setCurrentStep(Math.max(1, Math.min(step, LAST_STEP)));
    };

    const handleNextStep = () => goToStep(currentStep + 1);

    const handlePrevStep = () => goToStep(currentStep - 1);

    const mainImage = useMemo(() => images.find((i) => i.is_main) || images[0], [images]);

    // Arma el payload como FormData: las imágenes van como archivos binarios,
    // el resto de la estructura va como un campo JSON aparte. Este es el
    // formato típico que espera un backend con multer/formidable.
    const buildProductFormData = (payload: CreateProductPayload): FormData => {
        const fd = new FormData();
        // La posición final es el orden actual de la galería
        const imgs = payload.images.map((img, index) => ({ ...img, position: index }));
        const { images: _images, ...rest } = payload;

        // En edición: imágenes ya guardadas que se conservan (las que no vengan se eliminan)
        const images_existing = imgs
            .filter((img) => !img.file)
            .map((img) => ({ id: img.id, position: img.position, is_main: img.is_main, option_value_id: img.option_value_id || null }));

        fd.append('data', JSON.stringify({ ...rest, images_existing }));

        imgs.forEach((img) => {
            if (img.file) {
                // client_id permite que el backend te devuelva qué URL final
                // corresponde a cuál imagen temporal, para reconciliar is_main, etc.
                fd.append('images[]', img.file, img.file.name);
                fd.append(
                    'images_meta[]',
                    JSON.stringify({
                        client_id: img.id,
                        is_main: img.is_main,
                        position: img.position,
                        option_value_client_id: img.option_value_id || null,
                    })
                );
            }
        });

        return fd;
    };

    const handlePublish = () => {
        const toppingsError = validateToppingGroups(toppingGroups, toppingVariants);
        if (toppingsError) {
            showToast({ title: 'Revisa los toppings', message: toppingsError, type: 'warning', delay: 4000 });
            setCurrentStep(4);
            return;
        }

        const payload: CreateProductPayload = {
            product: formData,
            images,
            options,
            variants: formData.has_variants ? variants : [],
            addons: linkedAddons,
            // Solo viajan reglas de variantes que siguen existiendo (la matriz se puede regenerar)
            toppings_groups: toppingGroups.map((g) => ({
                ...g,
                variant_rules: g.variant_rules.filter((r) => toppingVariants.some((v) => v.id === r.variant_id)),
            })),
        };
        payload.product.brand = payload.product.brand || 'brand'; // TODO: quitar cuando el paso 1 pida brand real
        payload.product.products_units_measurement_id = payload.product.products_units_measurement_id || 1; // TODO: idem

        const formDataToSend = buildProductFormData(payload);

        // IMPORTANTE: se envía formDataToSend (contiene los archivos binarios), nunca el
        // objeto `payload` plano — un File no sobrevive un JSON.stringify.
        setSaving(true);
        const request = isEdit && productId
            ? DomicilappService.updateProductInStore(storeId, productId, formDataToSend)
            : DomicilappService.addProductToStore(storeId, formDataToSend);
        request
            .then((response: any) => {
                console.log('Response', response);
                // alert('¡Producto guardado exitosamente!');
                if (response.status == 200) { 
                    showToast({
                        title: '¡Operación Exitosa!',
                        message: isEdit ? 'Los cambios del producto se guardaron' : 'El producto se agregó de manera exitosa',
                        type: 'success', // 'success' | 'danger' | 'warning' | 'info' | 'brand'
                        delay: 3500,
                    });
                    navigate('/products/list');
                } else {
                    showToast({
                        title: '¡Ha ocurrido un error!',
                        // El backend deja el detalle de validación en data.error
                        message: typeof response?.data?.error === 'string' ? response.data.error : response?.message,
                        type: 'danger', // 'success' | 'danger' | 'warning' | 'info' | 'brand'
                        delay: 3500,
                    });
                }
           
            })
            .catch((err) => {
                console.error('Error al guardar el producto', err);
                showToast({ title: '¡Ha ocurrido un error!', message: 'Hubo un error guardando el producto', type: 'danger', delay: 3500 });
            })
            .finally(() => setSaving(false));
    };

    function capitalize(text: string): string {
        if (!text) return "";
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    return (
        <div className="product-create-wizard max-w-100 pb-5">
            {/* HEADER DE LA PÁGINA */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <div className="d-flex align-items-center gap-3">
                    <CButton
                        color="light"
                        onClick={() => navigate('/products/list')}
                        className="border text-dark rounded-circle p-2 d-flex align-items-center justify-content-center"
                        style={{ width: '40px', height: '40px' }}
                    >
                        <CIcon icon={cilArrowLeft} />
                    </CButton>
                    <div>
                        <h3 className="fw-bold text-dark mb-0 fs-4">{isEdit ? `Editar: ${formData.name || 'Producto'}` : 'Crear Nuevo Producto'}</h3>
                        <span className="text-muted small">
                            {isEdit ? 'Actualiza información, imágenes, variantes, toppings y adicionales' : 'Configuración general y matriz de opciones del catálogo'}
                        </span>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <span className="text-muted small">{formData.is_active ? 'Activo' : 'Borrador'}</span>
                    <CFormSwitch
                        checked={formData.is_active}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    />
                </div>
            </div>

            {/* STEPPER */}
            <CCard className="border shadow-sm rounded-3 mb-4 bg-white">
                <CCardBody className="p-3">
                    <div className="d-flex justify-content-between align-items-center position-relative px-2">
                        {[
                            { step: 1, label: 'General', icon: cilTag },
                            { step: 2, label: 'Estructura & Opciones', icon: cilLayers },
                            { step: 3, label: 'Matriz de Variantes', icon: cilListFilter },
                            { step: 4, label: 'Toppings', icon: cilLayers },
                            { step: 5, label: 'Adicionales', icon: cilPlus },
                            { step: 6, label: isEdit ? 'Guardar' : 'Publicar', icon: cilCheck },
                        ].map((s) => {
                            const isActive = currentStep === s.step;
                            const isCompleted = currentStep > s.step;
                            return (
                                <div
                                    key={s.step}
                                    className="d-flex align-items-center gap-2 cursor-pointer"
                                    // En edición todo ya está cargado: se puede saltar a cualquier paso
                                    onClick={() => (isEdit || s.step < currentStep) && goToStep(s.step)}
                                >
                                    <div
                                        className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${isActive ? 'bg-dark text-white' : isCompleted ? 'bg-light text-dark border' : 'bg-light text-muted border'
                                            }`}
                                        style={{ width: '36px', height: '36px' }}
                                    >
                                        <CIcon icon={s.icon} size="sm" />
                                    </div>
                                    <span
                                        className={`d-none d-md-inline ${isActive ? 'fw-bold text-dark' : isCompleted ? 'fw-semibold text-dark' : 'text-muted'}`}
                                        style={{ fontSize: '0.88rem' }}
                                    >
                                        {s.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </CCardBody>
            </CCard>

            {/* CONTENIDO SEGÚN PASO ACTUAL */}
            <CCard className="border shadow-sm rounded-3 bg-white mb-4">
                <CCardBody className="p-4">
                    {loadingProduct && (
                        <div className="text-center py-5">
                            <CSpinner color="primary" size="sm" className="me-2" />
                            <span className="text-muted">Cargando producto...</span>
                        </div>
                    )}
                    {/* ---------------- PASO 1: INFORMACIÓN GENERAL + GALERÍA ---------------- */}
                    {!loadingProduct && currentStep === 1 && (
                        <div>
                            <h5 className="fw-bold text-dark mb-3">Información General del Producto</h5>
                            <CRow className="g-3 mb-4">
                                <CCol md={8}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold text-dark small">Nombre del Producto</label>
                                        <CFormInput
                                            placeholder="Ej: Pizza Hawaiana, Camisa Polo..."
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        />
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold text-dark small">Descripción</label>
                                        <CFormTextarea
                                            rows={4}
                                            placeholder="Escribe los ingredientes principales o características..."
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        />
                                    </div>
                                </CCol>
                                <CCol md={4}>
                                    <div className="mb-3">
                                        <label className="form-label fw-semibold text-dark small">Categoría</label>
                                        <CFormSelect
                                            value={formData.category_id}
                                            className='text-capitalize'
                                            onChange={(e) => setFormData({ ...formData, category_id: Number(e.target.value) })}
                                        >
                                            {categories.map((c) => (
                                                <option key={c.id} value={c.id} className='text-capitalize'>
                                                    {(c.name)}
                                                </option>
                                            ))}
                                        </CFormSelect>
                                    </div>
                                </CCol>
                            </CRow>

                            {/* ---- GALERÍA DE IMÁGENES (product_images) — subida desde el PC ---- */}
                            <div className="d-flex justify-content-between align-items-center mb-2">
                                <h6 className="fw-bold text-dark mb-0">Galería de Imágenes</h6>
                                <span className="text-muted small">La imagen con ⭐ es la principal del producto</span>
                            </div>

                            <label
                                htmlFor="product-images-input"
                                className="d-flex flex-column align-items-center justify-content-center gap-2 border rounded-3 bg-light mb-3"
                                style={{ borderStyle: 'dashed', padding: '28px', cursor: 'pointer' }}
                            >
                                <CIcon icon={cilCloudUpload} size="xl" className="text-muted" />
                                <span className="fw-semibold text-dark small">Haz clic para subir imágenes</span>
                                <span className="text-muted small">o arrástralas aquí — PNG, JPG (puedes seleccionar varias)</span>
                                <input
                                    id="product-images-input"
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    className="d-none"
                                    onChange={(e) => {
                                        handleFilesSelected(e.target.files);
                                        e.target.value = '';
                                    }}
                                />
                            </label>

                            {images.length === 0 ? (
                                <div className="p-4 text-center bg-light rounded-3 border">
                                    <CIcon icon={cilImage} size="xl" className="text-muted mb-2" />
                                    <p className="mb-0 text-muted small">Aún no has subido imágenes del producto.</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-wrap gap-3">
                                    {images.map((img) => (
                                        <div key={img.id} className="border rounded-3 p-2 bg-white" style={{ width: '140px' }}>
                                            <div
                                                className="rounded-2 mb-2 bg-light d-flex align-items-center justify-content-center overflow-hidden"
                                                style={{ width: '100%', height: '100px' }}
                                            >
                                                <img
                                                    src={img.preview_url}
                                                    alt=""
                                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <CTooltip content={img.is_main ? 'Imagen principal' : 'Marcar como principal'}>
                                                    <CButton
                                                        color="link"
                                                        className={`p-0 ${img.is_main ? 'text-warning' : 'text-secondary'}`}
                                                        onClick={() => handleSetMainImage(img.id)}
                                                    >
                                                        <CIcon icon={cilStar} />
                                                    </CButton>
                                                </CTooltip>
                                                <CButton color="link" className="text-secondary p-0" onClick={() => handleRemoveImage(img.id)}>
                                                    <CIcon icon={cilTrash} />
                                                </CButton>
                                            </div>
                                            {formData.has_variants && (
                                                <CBadge
                                                    color={img.option_value_id ? 'dark' : 'light'}
                                                    className={`w-100 text-center fw-normal ${img.option_value_id ? 'text-white' : 'text-muted border'}`}
                                                >
                                                    {optionValueLabel(img.option_value_id) || 'Sin etiquetar'}
                                                </CBadge>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {formData.has_variants && images.length > 0 && (
                                <p className="text-muted small mt-2 mb-0">
                                    Ve al paso "Estructura & Opciones" para asociar cada imagen a un valor específico (ej. Color: Rojo).
                                </p>
                            )}
                        </div>
                    )}

                    {/* ---------------- PASO 2: ESTRUCTURA & OPCIONES ---------------- */}
                    {currentStep === 2 && (
                        <div>
                            <h5 className="fw-bold text-dark mb-1">Estructura del Producto</h5>
                            <p className="text-muted small mb-4">
                                Define si el producto tiene múltiples presentaciones/tamaños o es un ítem único.
                            </p>

                            <div className="p-3 bg-light rounded-3 mb-4 border">
                                <CFormCheck
                                    type="radio"
                                    name="productType"
                                    id="typeSimple"
                                    label="Producto Simple (Un solo precio y stock)"
                                    checked={!formData.has_variants}
                                    onChange={() => setFormData({ ...formData, has_variants: false })}
                                    className="mb-2 fw-semibold text-dark"
                                />
                                <CFormCheck
                                    type="radio"
                                    name="productType"
                                    id="typeVariants"
                                    label="Producto con Múltiples Opciones / Variantes (Ej: Tamaño, Color, Sabor)"
                                    checked={formData.has_variants}
                                    onChange={() => setFormData({ ...formData, has_variants: true })}
                                    className="fw-semibold text-dark"
                                />
                            </div>

                            {formData.has_variants ? (
                                <div>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h6 className="fw-bold text-dark mb-0">Ejes de Variación (Product Options)</h6>
                                    </div>

                                    {options.length === 0 && (
                                        <div className="p-3 mb-3 bg-light rounded-3 border text-muted small">
                                            Crea al menos una opción (ej. "Tamaño") y sus valores para poder generar variantes.
                                        </div>
                                    )}

                                    {(options.length > 1 || options.some((o) => o.values.length > 1)) && (
                                        <p className="text-muted small mb-2">
                                            Arrastra las opciones y sus valores desde <CIcon icon={cilMenu} size="sm" /> para ordenarlos: así los verá el cliente en la app.
                                            La primera opción es la que el cliente elige primero; las siguientes muestran solo los valores
                                            que existen con lo elegido (ej. pon <strong>Menú</strong> antes que <strong>Presentación</strong>).
                                        </p>
                                    )}

                                    {options.map((opt) => (
                                        <CCard
                                            key={opt.id}
                                            className={`border mb-3 rounded-3 shadow-none bg-white ${dragOverId === opt.id && dragging?.optionId !== opt.id ? 'border-primary' : ''}`}
                                            style={{ opacity: dragging && !dragging.valueId && dragging.optionId === opt.id ? 0.5 : 1 }}
                                            {...dragOverHandlers(opt.id)}
                                        >
                                            <CCardBody className="p-3">
                                                <div className="d-flex justify-content-between align-items-center mb-2">
                                                    <div className="d-flex align-items-center gap-2">
                                                        {options.length > 1 && (
                                                            <span
                                                                draggable
                                                                title="Arrastra para ordenar"
                                                                className="text-muted"
                                                                style={{ cursor: 'grab' }}
                                                                onDragStart={(e) => startDrag(e, { optionId: opt.id }, e.currentTarget.closest('.card'))}
                                                                onDragEnd={endDrag}
                                                            >
                                                                <CIcon icon={cilMenu} />
                                                            </span>
                                                        )}
                                                        <span className="fw-bold text-dark">{opt.name}</span>
                                                    </div>
                                                    <CButton
                                                        color="link"
                                                        className="text-secondary p-0 text-decoration-none"
                                                        onClick={() => handleRemoveOption(opt.id)}
                                                    >
                                                        <CIcon icon={cilTrash} />
                                                    </CButton>
                                                </div>

                                                <div className="d-flex flex-wrap gap-2 mb-3">
                                                    {opt.values.length === 0 && (
                                                        <span className="text-muted small">Sin valores todavía.</span>
                                                    )}
                                                    {opt.values.map((val) => (
                                                        <CBadge
                                                            key={val.id}
                                                            color="light"
                                                            className={`text-dark border p-2 d-flex align-items-center gap-2 fs-6 fw-normal ${dragOverId === val.id && dragging?.valueId !== val.id ? 'border-primary' : ''}`}
                                                            style={{ opacity: dragging?.valueId === val.id ? 0.5 : 1 }}
                                                            {...dragOverHandlers(opt.id, val.id)}
                                                        >
                                                            {/* Mismo patrón que las opciones: se arrastra desde el ícono y la vista previa es el valor */}
                                                            {opt.values.length > 1 && (
                                                                <span
                                                                    draggable
                                                                    title="Arrastra para ordenar"
                                                                    className="text-muted d-flex"
                                                                    style={{ cursor: 'grab' }}
                                                                    onDragStart={(e) => startDrag(e, { optionId: opt.id, valueId: val.id }, e.currentTarget.closest('.badge'))}
                                                                    onDragEnd={endDrag}
                                                                >
                                                                    <CIcon icon={cilMenu} size="sm" />
                                                                </span>
                                                            )}
                                                            <span>{val.value}</span>
                                                            <span
                                                                className="cursor-pointer text-muted fw-bold"
                                                                onClick={() => handleRemoveOptionValue(opt.id, val.id)}
                                                            >
                                                                ×
                                                            </span>
                                                        </CBadge>
                                                    ))}
                                                </div>

                                                <div className="d-flex gap-2" style={{ maxWidth: '350px' }}>
                                                    <CFormInput
                                                        size="sm"
                                                        placeholder={`Agregar valor a ${opt.name} (ej. Mediana)`}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') {
                                                                e.preventDefault();
                                                                handleAddOptionValue(opt.id, e.currentTarget.value);
                                                                e.currentTarget.value = '';
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            </CCardBody>
                                        </CCard>
                                    ))}

                                    <div className="d-flex gap-2 mt-3" style={{ maxWidth: '400px' }}>
                                        <CFormInput
                                            placeholder="Nueva opción (Ej: Talla, Material, Sabor)"
                                            value={newOptionName}
                                            onChange={(e) => setNewOptionName(e.target.value)}
                                            onKeyDown={(e) => e.key === 'Enter' && handleAddOption()}
                                        />
                                        <CButton color="dark" onClick={handleAddOption} className="text-white text-nowrap fw-semibold">
                                            + Crear Opción
                                        </CButton>
                                    </div>

                                    {/* ---- ETIQUETADO DE IMÁGENES POR VALOR DE OPCIÓN ---- */}
                                    {images.length > 0 && options.some((o) => o.values.length > 0) && (
                                        <div className="mt-4 pt-4 border-top">
                                            <h6 className="fw-bold text-dark mb-1">Imágenes por Valor de Opción</h6>
                                            <p className="text-muted small mb-3">
                                                Marca qué fotos de tu galería representan cada valor (ej. varias fotos del zapato "Rojo").
                                                Una imagen puede pertenecer a un solo valor a la vez.
                                            </p>
                                            {options
                                                .filter((opt) => opt.values.length > 0)
                                                .map((opt) => (
                                                    <div key={opt.id} className="mb-3">
                                                        <span className="fw-semibold text-dark small">{opt.name}</span>
                                                        {opt.values.map((val) => (
                                                            <div key={val.id} className="d-flex align-items-center gap-2 mt-2 flex-wrap">
                                                                <CBadge color="light" className="text-dark border fw-normal" style={{ minWidth: '90px' }}>
                                                                    {val.value}
                                                                </CBadge>
                                                                <div className="d-flex flex-wrap gap-2">
                                                                    {images.map((img) => {
                                                                        const selected = img.option_value_id === val.id;
                                                                        return (
                                                                            <div
                                                                                key={img.id}
                                                                                onClick={() => toggleImageOptionValue(img.id, val.id)}
                                                                                className="rounded-2 overflow-hidden"
                                                                                style={{
                                                                                    width: '48px',
                                                                                    height: '48px',
                                                                                    cursor: 'pointer',
                                                                                    border: selected ? '3px solid #000' : '1px solid #dee2e6',
                                                                                    opacity: img.option_value_id && !selected ? 0.35 : 1,
                                                                                }}
                                                                                title={img.option_value_id && !selected ? `Ya etiquetada como "${optionValueLabel(img.option_value_id)}"` : 'Clic para etiquetar'}
                                                                            >
                                                                                <img src={img.preview_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <CRow className="g-3">
                                    <CCol md={4}>
                                        <label className="form-label fw-semibold text-dark small">Precio Base ($)</label>
                                        <CFormInput
                                            type="number"
                                            value={formData.base_price}
                                            onChange={(e) => setFormData({ ...formData, base_price: Number(e.target.value) })}
                                        />
                                    </CCol>
                                    <CCol md={4}>
                                        <label className="form-label fw-semibold text-dark small">Stock Disponibilidad</label>
                                        <CFormInput
                                            type="number"
                                            value={formData.base_stock}
                                            onChange={(e) => setFormData({ ...formData, base_stock: Number(e.target.value) })}
                                        />
                                    </CCol>
                                    <CCol md={4}>
                                        <label className="form-label fw-semibold text-dark small">SKU</label>
                                        <CFormInput
                                            value={formData.base_sku}
                                            onChange={(e) => setFormData({ ...formData, base_sku: e.target.value })}
                                        />
                                    </CCol>
                                </CRow>
                            )}

                            {/* Aplica a todo el producto (simple o con variantes) */}
                            <CRow className="g-3 mt-1">
                                <CCol md={4}>
                                    <label className="form-label fw-semibold text-body small">Costo de empaque (solo domicilio)</label>
                                    <CFormInput
                                        type="number"
                                        min={0}
                                        value={formData.packaging_fee}
                                        onChange={(e) => setFormData({ ...formData, packaging_fee: Math.max(0, Number(e.target.value) || 0) })}
                                    />
                                </CCol>
                                <CCol md={8} className="d-flex align-items-end">
                                    <p className="text-body-secondary small mb-2">
                                        Se cobra por unidad solo cuando el pedido va a domicilio o programado. Déjalo en 0 si no cobras empaque.
                                    </p>
                                </CCol>
                            </CRow>
                        </div>
                    )}

                    {/* ---------------- PASO 3: MATRIZ DE VARIANTES ---------------- */}
                    {currentStep === 3 && (
                        <div>
                            <div className="d-flex justify-content-between align-items-start mb-1">
                                <div>
                                    <h5 className="fw-bold text-dark mb-1">Matriz de Variantes Combinadas</h5>
                                    <p className="text-muted small mb-0">
                                        Generadas automáticamente según tus opciones. Ajusta precio, stock e imagen de cada combinación.
                                        Si una combinación no existe en tu menú, quítala con <CIcon icon={cilTrash} size="sm" />; si solo está agotada, desmarca <strong>Activa</strong>.
                                    </p>
                                </div>
                                {formData.has_variants && (
                                    <div className="d-flex gap-2 flex-shrink-0">
                                        {removedCombos.size > 0 && (
                                            <CButton size="sm" color="light" className="border text-dark fw-semibold" onClick={restoreRemovedCombinations}>
                                                Restaurar quitadas ({removedCombos.size})
                                            </CButton>
                                        )}
                                        <CButton size="sm" color="light" className="border text-dark fw-semibold" onClick={() => generateVariantsFromOptions()}>
                                            <CIcon icon={cilReload} className="me-1" /> Regenerar matriz
                                        </CButton>
                                    </div>
                                )}
                            </div>

                            {!formData.has_variants ? (
                                <div className="p-4 text-center bg-light rounded-3 border mt-3">
                                    <p className="mb-0 text-muted">
                                        Este producto fue configurado como <strong>Simple</strong>. No requiere matriz de variantes.
                                    </p>
                                </div>
                            ) : variants.length === 0 ? (
                                <div className="p-4 text-center bg-light rounded-3 border mt-3">
                                    <p className="mb-0 text-muted">
                                        No hay combinaciones todavía. Vuelve al paso anterior y agrega opciones con al menos un valor.
                                    </p>
                                </div>
                            ) : (
                                <>
                                    <div className="d-flex flex-wrap align-items-end gap-2 p-3 bg-light rounded-3 border mt-3 mb-3">
                                        <div>
                                            <label className="form-label fw-semibold text-dark small mb-1">Aplicar precio a todas</label>
                                            <div className="d-flex gap-2">
                                                <CFormInput
                                                    size="sm"
                                                    type="number"
                                                    style={{ width: '140px' }}
                                                    placeholder="$"
                                                    value={bulkPrice}
                                                    onChange={(e) => setBulkPrice(e.target.value === '' ? '' : Number(e.target.value))}
                                                />
                                                <CButton size="sm" color="dark" className="text-white fw-semibold" onClick={applyBulkPrice}>
                                                    Aplicar
                                                </CButton>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="form-label fw-semibold text-dark small mb-1">Aplicar stock a todas</label>
                                            <div className="d-flex gap-2">
                                                <CFormInput
                                                    size="sm"
                                                    type="number"
                                                    style={{ width: '140px' }}
                                                    placeholder="Stock"
                                                    value={bulkStock}
                                                    onChange={(e) => setBulkStock(e.target.value === '' ? '' : Number(e.target.value))}
                                                />
                                                <CButton size="sm" color="dark" className="text-white fw-semibold" onClick={applyBulkStock}>
                                                    Aplicar
                                                </CButton>
                                            </div>
                                        </div>
                                        <span className="text-muted small ms-auto">
                                            {variants.length} combinaciones{removedCombos.size > 0 ? ` · ${removedCombos.size} quitadas` : ''}
                                        </span>
                                    </div>

                                    {orphanValues.length > 0 && (
                                        <div className="small text-body-secondary bg-body-tertiary border rounded-3 p-2 mb-3">
                                            Estos valores no quedaron en ninguna combinación y el cliente no podrá elegirlos:{' '}
                                            <strong>{orphanValues.join(', ')}</strong>. Elimínalos en el paso 2 o restaura sus combinaciones.
                                        </div>
                                    )}

                                    <CTable responsive align="middle" bordered className="bg-white">
                                        <CTableHead color="light">
                                            <CTableRow>
                                                <CTableHeaderCell className="text-secondary small fw-bold">Combinación</CTableHeaderCell>
                                                <CTableHeaderCell className="text-secondary small fw-bold" style={{ width: '180px' }}>SKU</CTableHeaderCell>
                                                <CTableHeaderCell className="text-secondary small fw-bold" style={{ width: '140px' }}>Precio ($)</CTableHeaderCell>
                                                <CTableHeaderCell className="text-secondary small fw-bold" style={{ width: '110px' }}>Stock</CTableHeaderCell>
                                                <CTableHeaderCell className="text-secondary small fw-bold" style={{ width: '170px' }}>Imagen</CTableHeaderCell>
                                                <CTableHeaderCell className="text-secondary small fw-bold text-center" style={{ width: '90px' }}>Activa</CTableHeaderCell>
                                                <CTableHeaderCell style={{ width: '50px' }}></CTableHeaderCell>
                                            </CTableRow>
                                        </CTableHead>
                                        <CTableBody>
                                            {variants.map((v) => (
                                                <React.Fragment key={v.id}>
                                                    <CTableRow>
                                                        <CTableDataCell>
                                                            <div className="d-flex flex-wrap gap-1 mb-1">
                                                                {Object.entries(v.options_map).map(([k, val]) => (
                                                                    <CBadge key={k} color="light" className="text-dark border fw-normal">
                                                                        {k}: <strong>{val}</strong>
                                                                    </CBadge>
                                                                ))}
                                                            </div>
                                                            <CButton
                                                                color="link"
                                                                size="sm"
                                                                className="p-0 text-decoration-none small"
                                                                onClick={() => toggleDescriptionRow(v.id)}
                                                            >
                                                                {v.description
                                                                    ? '✓ Descripción agregada (editar)'
                                                                    : '+ Agregar descripción específica'}
                                                            </CButton>
                                                        </CTableDataCell>
                                                        <CTableDataCell>
                                                            <div className="d-flex gap-1">
                                                                <CFormInput
                                                                    size="sm"
                                                                    value={v.sku}
                                                                    onChange={(e) => updateVariant(v.id, { sku: e.target.value })}
                                                                />
                                                                <CTooltip content="Regenerar SKU automático">
                                                                    <CButton size="sm" color="light" className="border" onClick={() => regenerateSku(v.id)}>
                                                                        <CIcon icon={cilReload} size="sm" />
                                                                    </CButton>
                                                                </CTooltip>
                                                            </div>
                                                        </CTableDataCell>
                                                        <CTableDataCell>
                                                            <CFormInput
                                                                size="sm"
                                                                type="number"
                                                                value={v.price}
                                                                onChange={(e) => updateVariant(v.id, { price: Number(e.target.value) })}
                                                            />
                                                        </CTableDataCell>
                                                        <CTableDataCell>
                                                            <CFormInput
                                                                size="sm"
                                                                type="number"
                                                                value={v.stock}
                                                                onChange={(e) => updateVariant(v.id, { stock: Number(e.target.value) })}
                                                            />
                                                        </CTableDataCell>
                                                        <CTableDataCell>
                                                            {(() => {
                                                                const resolved = resolveVariantImage(v);
                                                                return resolved ? (
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <img
                                                                            src={resolved.preview_url}
                                                                            alt=""
                                                                            className="rounded-2"
                                                                            style={{ width: '32px', height: '32px', objectFit: 'cover' }}
                                                                        />
                                                                        <span className="text-muted small">
                                                                            {optionValueLabel(resolved.option_value_id) || 'Principal'}
                                                                        </span>
                                                                    </div>
                                                                ) : (
                                                                    <span className="text-muted small">Sin imagen</span>
                                                                );
                                                            })()}
                                                        </CTableDataCell>
                                                        <CTableDataCell className="text-center">
                                                            <CFormCheck
                                                                checked={v.is_available}
                                                                onChange={(e) => updateVariant(v.id, { is_available: e.target.checked })}
                                                            />
                                                        </CTableDataCell>
                                                        <CTableDataCell className="text-center">
                                                            <CTooltip content="Quitar: esta combinación no existe">
                                                                <CButton size="sm" color="light" className="border" onClick={() => removeCombination(v)}>
                                                                    <CIcon icon={cilTrash} size="sm" />
                                                                </CButton>
                                                            </CTooltip>
                                                        </CTableDataCell>
                                                    </CTableRow>
                                                    {expandedDescriptions.has(v.id) && (
                                                        <CTableRow>
                                                            <CTableDataCell colSpan={7} className="bg-light">
                                                                <label className="form-label fw-semibold text-dark small mb-1">
                                                                    Descripción específica de esta variante (opcional)
                                                                </label>
                                                                <CFormTextarea
                                                                    rows={2}
                                                                    // size="sm"
                                                                    placeholder="Ej: Trae doble carne, huevo frito y tocineta. Ideal para 2 personas."
                                                                    value={v.description || ''}
                                                                    onChange={(e) => updateVariant(v.id, { description: e.target.value })}
                                                                />
                                                            </CTableDataCell>
                                                        </CTableRow>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                        </CTableBody>
                                    </CTable>
                                </>
                            )}
                        </div>
                    )}

                    {/* ---------------- PASO 4: TOPPINGS ---------------- */}
                    {!loadingProduct && currentStep === 4 && (
                        <ToppingsStep storeId={storeId} groups={toppingGroups} onChange={setToppingGroups} variants={toppingVariants} />
                    )}

                    {/* ---------------- PASO 5: ADICIONALES (addons + product_addons) ---------------- */}
                    {currentStep === 5 && (
                        <div>
                            <h5 className="fw-bold text-dark mb-1">Adicionales del Producto</h5>
                            <p className="text-muted small mb-4">
                                Reutiliza adicionales del catálogo general (Queso Extra, Papa Francesa, Gaseosa...) o crea uno nuevo si no existe.
                            </p>

                            <CRow className="g-3">
                                {/* CATÁLOGO EXISTENTE */}
                                <CCol md={6}>
                                    <h6 className="fw-bold text-dark mb-2">Catálogo de Adicionales</h6>
                                    <div className="position-relative mb-2">
                                        <CIcon icon={cilSearch} className="position-absolute text-muted" style={{ left: '10px', top: '10px' }} />
                                        <CFormInput
                                            style={{ paddingLeft: '32px' }}
                                            placeholder="Buscar en el catálogo..."
                                            value={addonSearch}
                                            onChange={(e) => setAddonSearch(e.target.value)}
                                        />
                                    </div>

                                    <div className="border rounded-3 mb-3" style={{ maxHeight: '260px', overflowY: 'auto' }}>
                                        {filteredCatalog.length === 0 ? (
                                            <p className="text-muted small p-3 mb-0">No hay adicionales que coincidan.</p>
                                        ) : (
                                            filteredCatalog.map((addon) => (
                                                <div
                                                    key={addon.id}
                                                    className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom"
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => toggleLinkAddon(addon)}
                                                >
                                                    <CFormCheck checked={isLinked(addon.id)} onChange={() => toggleLinkAddon(addon)} label={addon.name} className="fw-semibold text-dark small mb-0" />
                                                    <span className="text-muted small">+ ${addon.price}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>

                                    <div className="p-3 bg-light rounded-3 border">
                                        <p className="fw-semibold text-dark small mb-2">¿No existe? Crea uno nuevo</p>
                                        <div className="d-flex flex-wrap gap-2">
                                            <CFormInput
                                                placeholder="Nombre (Ej: Salsa BBQ)"
                                                value={newAddonName}
                                                onChange={(e) => setNewAddonName(e.target.value)}
                                                style={{ flex: '1 1 160px' }}
                                            />
                                            <CFormInput
                                                type="number"
                                                placeholder="Precio"
                                                value={newAddonPrice}
                                                onChange={(e) => setNewAddonPrice(Number(e.target.value))}
                                                style={{ width: '110px' }}
                                            />
                                            <CButton
                                                color="dark"
                                                className="text-white fw-semibold text-nowrap"
                                                onClick={handleCreateAddon}
                                                disabled={creatingAddon}
                                            >
                                                <CIcon icon={cilPlus} className="me-1" /> {creatingAddon ? 'Creando...' : 'Crear y asignar'}
                                            </CButton>
                                        </div>
                                    </div>
                                </CCol>

                                {/* ADICIONALES ASIGNADOS A ESTE PRODUCTO */}
                                <CCol md={6}>
                                    <h6 className="fw-bold text-dark mb-2">Asignados a este producto ({linkedAddons.length})</h6>
                                    {linkedAddons.length === 0 ? (
                                        <div className="p-4 text-center bg-light rounded-3 border">
                                            <p className="mb-0 text-muted small">Selecciona adicionales del catálogo a la izquierda.</p>
                                        </div>
                                    ) : (
                                        <CTable align="middle" hover className="border">
                                            <CTableHead color="light">
                                                <CTableRow>
                                                    <CTableHeaderCell className="text-secondary small fw-bold">Nombre</CTableHeaderCell>
                                                    <CTableHeaderCell className="text-secondary small fw-bold">Precio</CTableHeaderCell>
                                                    <CTableHeaderCell className="text-secondary small fw-bold text-center">Por defecto</CTableHeaderCell>
                                                    <CTableHeaderCell className="text-secondary small fw-bold text-end">Acción</CTableHeaderCell>
                                                </CTableRow>
                                            </CTableHead>
                                            <CTableBody>
                                                {linkedAddons.map((link) => (
                                                    <CTableRow key={link.id}>
                                                        <CTableDataCell className="fw-semibold text-dark small">{link.name}</CTableDataCell>
                                                        <CTableDataCell className="small">+ ${link.price}</CTableDataCell>
                                                        <CTableDataCell className="text-center">
                                                            <CFormCheck checked={link.is_default} onChange={() => toggleDefaultAddon(link.id.toString())} />
                                                        </CTableDataCell>
                                                        <CTableDataCell className="text-end">
                                                            <CButton color="link" className="text-secondary p-0" onClick={() => removeLinkedAddon(link.id.toString())}>
                                                                <CIcon icon={cilTrash} />
                                                            </CButton>
                                                        </CTableDataCell>
                                                    </CTableRow>
                                                ))}
                                            </CTableBody>
                                        </CTable>
                                    )}
                                    <p className="text-muted small mt-2 mb-0">
                                        "Por defecto" marca este adicional como premarcado cuando el cliente arma su pedido (ej. combos).
                                    </p>
                                </CCol>
                            </CRow>
                        </div>
                    )}

                    {/* ---------------- PASO 6: RESUMEN Y CONFIRMACIÓN ---------------- */}
                    {currentStep === 6 && (
                        <div>
                            <h5 className="fw-bold text-dark mb-3">Resumen de Publicación</h5>
                            <CRow className="g-3">
                                <CCol md={7}>
                                    <div className="p-3 border rounded-3 bg-light mb-3">
                                        <p className="mb-1 text-dark"><strong>Nombre:</strong> {formData.name || 'Sin nombre'}</p>
                                        <p className="mb-1 text-dark"><strong>Categoría:</strong> {categories.find((c) => c.id === formData.category_id)?.name}</p>
                                        <p className="mb-1 text-dark"><strong>Descripción:</strong> {formData.description || 'Sin descripción'}</p>
                                        <p className="mb-1 text-dark"><strong>Estado:</strong> {formData.is_active ? 'Activo' : 'Borrador'}</p>
                                        <p className="mb-0 text-dark"><strong>Tipo:</strong> {formData.has_variants ? 'Con Variantes' : 'Producto Simple'}</p>
                                    </div>

                                    {formData.has_variants && variants.length > 0 && (
                                        <CTable small bordered className="bg-white">
                                            <CTableHead color="light">
                                                <CTableRow>
                                                    <CTableHeaderCell className="small">Variante</CTableHeaderCell>
                                                    <CTableHeaderCell className="small">SKU</CTableHeaderCell>
                                                    <CTableHeaderCell className="small">Precio</CTableHeaderCell>
                                                    <CTableHeaderCell className="small">Stock</CTableHeaderCell>
                                                </CTableRow>
                                            </CTableHead>
                                            <CTableBody>
                                                {variants.map((v) => (
                                                    <CTableRow key={v.id}>
                                                        <CTableDataCell className="small">{Object.values(v.options_map).join(' / ')}</CTableDataCell>
                                                        <CTableDataCell className="small">{v.sku}</CTableDataCell>
                                                        <CTableDataCell className="small">${v.price}</CTableDataCell>
                                                        <CTableDataCell className="small">{v.stock}</CTableDataCell>
                                                    </CTableRow>
                                                ))}
                                            </CTableBody>
                                        </CTable>
                                    )}
                                </CCol>
                                <CCol md={5}>
                                    <div className="p-3 border rounded-3 bg-light mb-3">
                                        <p className="mb-1 text-dark"><strong>Imágenes:</strong> {images.length}</p>
                                        <p className="mb-1 text-dark"><strong>Opciones:</strong> {options.length}</p>
                                        <p className="mb-1 text-dark"><strong>Total Variantes:</strong> {formData.has_variants ? variants.length : 1}</p>
                                        <p className="mb-1 text-dark"><strong>Adicionales asignados:</strong> {linkedAddons.length}</p>
                                        <p className="mb-0 text-dark">
                                            <strong>Grupos de toppings:</strong> {toppingGroups.length}
                                            {toppingGroups.length > 0 && (
                                                <span className="text-muted small"> ({toppingGroups.map((g) => `${g.name || 'Sin nombre'}: ${g.items.length}`).join(', ')})</span>
                                            )}
                                        </p>
                                    </div>
                                    {mainImage && (
                                        <div className="border rounded-3 p-2 bg-white" style={{ width: '140px' }}>
                                            <img src={mainImage.preview_url} alt="" style={{ width: '100%', height: '100px', objectFit: 'cover', borderRadius: '6px' }} />
                                            <p className="text-muted small text-center mb-0 mt-1">Imagen principal</p>
                                        </div>
                                    )}
                                </CCol>
                            </CRow>
                        </div>
                    )}

                    {/* BOTONES DE NAVEGACIÓN INFERIOR */}
                    <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                        <CButton
                            color="light"
                            disabled={currentStep === 1 || loadingProduct}
                            onClick={handlePrevStep}
                            className="border fw-semibold text-dark"
                        >
                            <CIcon icon={cilArrowLeft} className="me-1" /> Anterior
                        </CButton>

                        {currentStep < LAST_STEP ? (
                            <CButton color="dark" onClick={handleNextStep} disabled={loadingProduct} className="text-white fw-semibold px-4">
                                Siguiente <CIcon icon={cilArrowRight} className="ms-1" />
                            </CButton>
                        ) : (
                            <CButton color="dark" onClick={handlePublish} disabled={saving} className="text-white fw-semibold px-4">
                                {saving ? <CSpinner size="sm" className="me-1" /> : <CIcon icon={cilCheck} className="me-1" />}
                                {isEdit ? 'Guardar Cambios' : 'Publicar Producto'}
                            </CButton>
                        )}
                    </div>
                </CCardBody>
            </CCard>
        </div>
    );
};

// Crear y editar comparten el wizard: se remonta al cambiar de producto (o de editar a crear)
// para no arrastrar el estado del formulario anterior
const ProductFormPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    return <ProductCreateWizard key={id ?? 'new'} />;
};

export default ProductFormPage;